import React from 'react';
import { Box, Typography, IconButton, Stack, Button, Chip, Pagination, Alert, Tabs, Tab, Divider, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Link } from '@mui/material';
import { Info as InfoIcon, ShoppingCart, SupportAgent, People, LockReset, CheckCircle, MarkEmailRead, Delete, AddShoppingCart } from '@mui/icons-material';
import SummarizeIcon from "@mui/icons-material/Summarize";
import { fetchCcrSubmission, fetchPurchaseRequestSubmission } from '../../services/notifications';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageTitle from '../../components/PageTitle/PageTitle';
import { formatDateTime } from "../../utilities/globalServiceUtils";
import ContextFilterAndSort from '../../components/ContextFilterAndSort/ContextFilterAndSort';
import { getNotificationNavigationTarget } from '../../utilities/notificationNavigation';
import { useNotifications } from '../../context/NotificationContext';
import { useSite } from '../../context/siteContext';
import { useCart } from '../../context/cartContext';
import { useAuth } from '../../context/authContext';
import { saveRequestListBulk } from '../../services/rsplService';
import Swal from 'sweetalert2';
import ModalWrapper from '../../components/ModalWrapper/ModalWrapper';
import { NotificationEvents } from '../../constants/notificationEvents';
import { getProductsById } from '../../services/cartService';
import { ROUTES } from '../../constants';
import formatCurrency from "../../utilities/currency";
import { ASSETS_BASE_URL } from "../../config";
import useBrandingAssets from "../../hooks/useBrandingAssets";

const iconFor = (type) => {
	const map = {
		ADMIN_NEW_ORDER_PLACED: <ShoppingCart color="success" />,
		USER_ORDER_SUCCESS: <ShoppingCart color="success" />,
		ADMIN_NOTIFY_CUSTOMER_CARE: <SupportAgent color="secondary" />,
		ADMIN_NEW_USER_REGISTERED: <People color="primary" />,
		USER_FORGOT_PASSWORD: <LockReset color="info" />,
		USER_REQUESTLIST_PURCHASABLE: <CheckCircle color="success" />,
	};
	return map[type] || <InfoIcon color="action" />;
};

const buildPurchaseRequestCardSummary = (notification = {}) => {
	const data = notification?.data || {};
	let requestedBy = String(data?.requested_by_name || "").trim();
	if (!requestedBy) {
		const body = String(notification?.body || "");
		const match = body.match(/^(.+?)\s+requested help placing an order\./i);
		requestedBy = String(match?.[1] || "").trim();
	}
	const message = String(data?.message || "").trim();
	const items = Array.isArray(data?.items) ? data.items : [];
	const productCount = items.length;
	const totalFromData = String(data?.total_amount || "").trim();
	let total = totalFromData;
	if (!total) {
		const msg = message || String(notification?.body || "");
		const m = msg.match(/total\s*amount\s*:\s*([^\.\n]+)/i);
		if (m?.[1]) total = m[1].trim();
	}

	const parts = [];
	if (requestedBy) parts.push(`Requested by: ${requestedBy}`);
	if (message) parts.push(`Message: ${message}`);
	if (productCount > 0) parts.push(`Parts: ${productCount}`);
	if (total) parts.push(`Total: ${total}`);
	return parts.join(" • ");
};

const getPurchaseRequestSender = (notification = {}, submission = null) => {
	const bySubmission = String(submission?.requested_by?.user_name || "").trim();
	if (bySubmission) return bySubmission;
	const byData = String(notification?.data?.requested_by_name || "").trim();
	if (byData) return byData;
	const body = String(notification?.body || "");
	const match = body.match(/^(.+?)\s+requested help placing an order\./i);
	return String(match?.[1] || "").trim();
};

const NotificationsPage = () => {
	const { t } = useTranslation();
	const PAGE_SIZE = 10;
	const [page, setPage] = React.useState(1);
	const [sort, setSort] = React.useState(t('GEOGRAPHIC.NEWEST_FIRST'));
	const [q, setQ] = React.useState('');
	const [tab, setTab] = React.useState('unread');
	const [pageCount, setPageCount] = React.useState(1);
	const [ccrModalOpen, setCcrModalOpen] = React.useState(false);
	const [ccrSubmission, setCcrSubmission] = React.useState(null);
	const [purchaseRequestModalOpen, setPurchaseRequestModalOpen] = React.useState(false);
	const [activePurchaseRequestNotification, setActivePurchaseRequestNotification] = React.useState(null);
	const [purchaseRequestSubmission, setPurchaseRequestSubmission] = React.useState(null);
	const [purchaseRequestLoading, setPurchaseRequestLoading] = React.useState(false);
	const [purchaseRequestProductMeta, setPurchaseRequestProductMeta] = React.useState({});
	const [addingToCart, setAddingToCart] = React.useState(false);
	const [addingToRequestList, setAddingToRequestList] = React.useState(false);
	const { items, unread, markAllRead, markOneRead, deleteRecipients, refreshNotifications } = useNotifications();
	const { selectedSite, selectedSiteCurrency, siteData, updateSite } = useSite();
	const { stockImageUrl } = useBrandingAssets();
	const { addMultipleToCart } = useCart();
	const { user } = useAuth();
	const formatTime = (ts) => {
		try {
			const d = new Date(ts);
			const now = new Date();
			const diffMs = now - d;
			const sec = Math.floor(diffMs / 1000);
			if (sec < 60) return 'now';
			const min = Math.floor(sec / 60);
			if (min < 60) return `${min} mins ago`;
			const hr = Math.floor(min / 60);
			const y = new Date(now); y.setDate(now.getDate() - 1);
			const isYesterday = d.getDate() === y.getDate() && d.getMonth() === y.getMonth() && d.getFullYear() === y.getFullYear();
			if (isYesterday) return `yesterday at ${formatDateTime(d)}`;
			if (hr < 24) return `${hr} hours ago`;
			return formatDateTime(d);
		} catch { return String(ts); }
	};
	const navigate = useNavigate();

	const load = async () => {
		await refreshNotifications(500, 0);
	};

	React.useEffect(() => { load(); setPage(1); }, []);

	// Reload when tab changes
	React.useEffect(() => { setPage(1); }, [tab]);

	const sortedItems = React.useMemo(() => {
		const arr = Array.isArray(items) ? [...items] : [];
		arr.sort((a, b) => {
			const da = new Date(a.created_at).getTime();
			const db = new Date(b.created_at).getTime();
			return sort === t('GEOGRAPHIC.NEWEST_FIRST') ? (db - da) : (da - db);
		});
		return arr;
	}, [items, sort, t]);

	const filteredItems = React.useMemo(() => {
		const statusFiltered = sortedItems.filter((item) => (tab === 'unread' ? !item.read_at : !!item.read_at));
		const scopeFiltered = statusFiltered.filter((item) => {
			const data = item?.data || {};
			const siteNumber =
				data.site_number ||
				data.siteNumber ||
				data.site ||
				data.site_code ||
				data.siteCode ||
				(() => {
					const siteId = data.site_id || data.siteId || data.site_id_fk || null;
					if (!siteId || !Array.isArray(siteData)) return null;
					const match = siteData.find((s) => Number(s.site_id) === Number(siteId));
					return match?.customer_site_number ? String(match.customer_site_number) : null;
				})();
			if (!siteNumber) return true;
			if (!Array.isArray(siteData) || siteData.length === 0) return false;
			return siteData.some((s) => {
				const siteNum = s?.customer_site_number || s?.site_number || s?.site || s?.site_code;
				return siteNum && String(siteNum) === String(siteNumber);
			});
		});
		const normalizedQ = String(q || "").trim().toLowerCase();
		if (!normalizedQ) return scopeFiltered;
		return scopeFiltered.filter((item) => {
			const haystack = [
				item.title,
				item.body,
				item.description,
				item.event_key,
				item?.data?.description,
				item?.data?.message,
			].filter(Boolean).join(" ").toLowerCase();
			return haystack.includes(normalizedQ);
		});
	}, [sortedItems, tab, q, siteData]);

	const readCount = React.useMemo(
		() => sortedItems.filter((item) => !!item.read_at).length,
		[sortedItems]
	);

	const paginatedItems = React.useMemo(() => {
		const offset = (page - 1) * PAGE_SIZE;
		return filteredItems.slice(offset, offset + PAGE_SIZE);
	}, [filteredItems, page, PAGE_SIZE]);

	React.useEffect(() => {
		const pages = filteredItems.length > 0 ? Math.ceil(filteredItems.length / PAGE_SIZE) : 1;
		setPageCount(pages);
		if (page > pages) {
			setPage(1);
		}
	}, [filteredItems.length, page, PAGE_SIZE]);

	const markOne = async (recipient_id) => {
		if (!recipient_id) return;
		await markOneRead(recipient_id);
	};

	const deleteOne = async (recipient_id) => {
		if (!recipient_id) return;
		await deleteRecipients([recipient_id]);
	};

	const markAllLocal = async () => {
		await markAllRead();
	};

	const handlePageChange = (_e, p) => {
		setPage(p);
	};

	const sortOptions = [t('GEOGRAPHIC.NEWEST_FIRST'), t('GEOGRAPHIC.OLDEST_FIRST')];
	const resolveNotificationSiteNumber = React.useCallback((notification) => {
		const data = notification?.data || {};
		const siteNumber =
			data.site_number ||
			data.siteNumber ||
			data.site ||
			data.site_code ||
			data.siteCode ||
			null;
		if (siteNumber) return String(siteNumber);
		const siteId =
			data.site_id ||
			data.siteId ||
			data.site_id_fk ||
			null;
		if (!siteId || !Array.isArray(siteData)) return null;
		const match = siteData.find((s) => Number(s.site_id) === Number(siteId));
		return match?.customer_site_number ? String(match.customer_site_number) : null;
	}, [siteData]);

	const isDifferentSite = React.useCallback((notification) => {
		const notifSite = resolveNotificationSiteNumber(notification);
		if (!notifSite || !selectedSite) return false;
		return String(notifSite) !== String(selectedSite);
	}, [resolveNotificationSiteNumber, selectedSite]);

	const hasAccessToNotificationSite = React.useCallback((notification) => {
		const notifSite = resolveNotificationSiteNumber(notification);
		if (!notifSite) return true;
		if (!Array.isArray(siteData) || siteData.length === 0) return false;
		return siteData.some((s) => {
			const siteNum = s?.customer_site_number || s?.site_number || s?.site || s?.site_code;
			return siteNum && String(siteNum) === String(notifSite);
		});
	}, [resolveNotificationSiteNumber, siteData]);

	const isPurchaseRequestNotification = React.useCallback((notification) => {
		const eventKey = String(notification?.event_key || "").toUpperCase();
		return eventKey === NotificationEvents.USER_PURCHASE_REQUEST_SUBMITTED;
	}, []);

	const resolveProductImageSrc = React.useCallback((item = {}) => {
		const raw = item?.asset_data?.images?.[0];
		if (raw) {
			if (/^(https?:)?\/\//.test(raw) || raw.startsWith("data:")) return raw;
			const imgPath = raw.startsWith("/") ? raw : `/${raw}`;
			return `${ASSETS_BASE_URL}${imgPath}`;
		}
		return `${ASSETS_BASE_URL}/ProductImages/TMB/${item.product_number}.JPG`;
	}, []);

	const getPurchaseRequestItems = React.useCallback((notification, submission = null) => {
		const rawItems = submission?.products || notification?.data?.items;
		if (!Array.isArray(rawItems)) return [];
		return rawItems
			.map((item) => {
				const productId = Number(item?.product_id);
				const quantity = Number(item?.quantity || 1);
				const machineId = Number(item?.machine_id);
				const meta = purchaseRequestProductMeta?.[productId] || {};
				const rawPurchasable = meta?.purchasable ?? item?.purchasable;
				const purchasable =
					rawPurchasable === true ||
					rawPurchasable === 1 ||
					String(rawPurchasable).toLowerCase() === "1" ||
					String(rawPurchasable).toLowerCase() === "true";
				if (!Number.isFinite(productId) || productId <= 0) return null;
				if (!Number.isFinite(quantity) || quantity <= 0) return null;
				return {
					product_id: productId,
					machine_id: Number.isFinite(machineId) && machineId > 0 ? machineId : (Number(meta?.machine_id) || null),
					product_number: String(item?.product_number || meta?.product_number || "").trim(),
					product_name: String(item?.product_name || meta?.product_name || "").trim(),
					asset_data: meta?.asset_data || null,
					quantity,
					purchasable,
					unit_price: Number(meta?.[`price_${selectedSiteCurrency}`] ?? 0) || 0,
				};
			})
			.filter(Boolean);
	}, [purchaseRequestProductMeta, selectedSiteCurrency]);

	const resolvePurchaseRequestSiteNumber = React.useCallback((notification, submission = null) => {
		const fromSubmission = submission?.site?.site_number;
		if (fromSubmission) return String(fromSubmission);
		return resolveNotificationSiteNumber(notification);
	}, [resolveNotificationSiteNumber]);

	const handleNavigateNotification = React.useCallback(async (notification, explicitTarget) => {
		const eventKey = String(notification?.event_key || "").toUpperCase();
		const isCcrEvent = eventKey.includes("CONTACT_CCR");
		if (isCcrEvent) {
			setCcrSubmission(null);
			setCcrModalOpen(true);
			const submissionId = notification?.data?.ccr_submission_id;
			if (submissionId) {
				try {
					const submission = await fetchCcrSubmission(submissionId);
					if (submission) setCcrSubmission(submission);
				} catch { }
			}
			return;
		}
		if (!hasAccessToNotificationSite(notification)) {
			const notifSite = resolveNotificationSiteNumber(notification);
			await Swal.fire({
				icon: 'warning',
				title: 'Access removed',
				text: `You no longer have access to the ${notifSite || 'selected'} location. Please contact system administrator for further details.`,
				toast: true,
				position: 'top-end',
				timer: 4000,
				showConfirmButton: false,
			});
			return;
		}
		const target = explicitTarget || getNotificationNavigationTarget(notification);
		if (!target) return;
		const notifSite = resolveNotificationSiteNumber(notification);
		const shouldPrompt = notifSite && selectedSite && String(notifSite) !== String(selectedSite) && !/^https?:/i.test(target);
		if (shouldPrompt) {
			const result = await Swal.fire({
				icon: 'warning',
				title: 'Switch Customer Location?',
				text: 'This will navigate the user to a different site, the site where this order was placed from.',
				showCancelButton: true,
				confirmButtonText: 'Yes, Switch Customer Location',
				cancelButtonText: t('BUTTON.CANCEL'),
			});
			if (!result.isConfirmed) return;
			updateSite(String(notifSite), siteData || []);
		}
		if (/^https?:/i.test(target)) {
			window.location.href = target;
		} else {
			navigate(target);
		}
	}, [navigate, resolveNotificationSiteNumber, selectedSite, siteData, updateSite, hasAccessToNotificationSite]);

	const handleOpenPurchaseRequestNotification = React.useCallback(async (notification) => {
		setActivePurchaseRequestNotification(notification || null);
		setPurchaseRequestSubmission(null);
		setPurchaseRequestProductMeta({});
		setPurchaseRequestModalOpen(true);

		const submissionId = notification?.data?.purchase_request_submission_id;
		setPurchaseRequestLoading(true);
		try {
			let submission = null;
			if (submissionId) {
				submission = await fetchPurchaseRequestSubmission(submissionId);
				if (submission) setPurchaseRequestSubmission(submission);
			}

			const siteNumber = resolvePurchaseRequestSiteNumber(notification, submission);
			const rows = submission?.products || notification?.data?.items || [];
			const productIds = Array.from(
				new Set(
					(Array.isArray(rows) ? rows : [])
						.map((item) => Number(item?.product_id))
						.filter((id) => Number.isFinite(id) && id > 0)
				)
			);
			if (siteNumber && productIds.length) {
				const productsResp = await getProductsById(productIds, siteNumber);
				const products = Array.isArray(productsResp?.data) ? productsResp.data : [];
				const metaMap = {};
				products.forEach((product) => {
					const id = Number(product?.product_id);
					if (Number.isFinite(id) && id > 0) metaMap[id] = product;
				});
				setPurchaseRequestProductMeta(metaMap);
			}
		} finally {
			setPurchaseRequestLoading(false);
		}
	}, [resolvePurchaseRequestSiteNumber]);

	const ensurePurchaseRequestSiteReady = React.useCallback(async (notification, submission = null) => {
		if (!hasAccessToNotificationSite(notification)) {
			const notifSite = resolvePurchaseRequestSiteNumber(notification, submission);
			await Swal.fire({
				icon: 'warning',
				title: 'Access removed',
				text: `You no longer have access to the ${notifSite || 'selected'} location. Please contact system administrator for further details.`,
				toast: true,
				position: 'top-end',
				timer: 4000,
				showConfirmButton: false,
			});
			return null;
		}

		const notifSite = resolvePurchaseRequestSiteNumber(notification, submission);
		if (!notifSite) {
			await Swal.fire({
				icon: 'warning',
				title: 'Missing site',
				text: 'Cannot continue because notification site is missing.',
			});
			return null;
		}

		const shouldPrompt = selectedSite && String(notifSite) !== String(selectedSite);
		if (shouldPrompt) {
			const result = await Swal.fire({
				icon: 'warning',
				title: 'Switch Customer Location?',
				text: 'This action will apply to the notification site.',
				showCancelButton: true,
				confirmButtonText: 'Yes, Switch Customer Location',
				cancelButtonText: t('BUTTON.CANCEL'),
			});
			if (!result.isConfirmed) return null;
			updateSite(String(notifSite), siteData || []);
		}
		return String(notifSite);
	}, [hasAccessToNotificationSite, resolvePurchaseRequestSiteNumber, selectedSite, siteData, t, updateSite]);

	const handleAddPurchaseRequestItemsToCart = React.useCallback(async (items) => {
		const notification = activePurchaseRequestNotification;
		if (!notification) return;
		if (!items.length) {
			await Swal.fire({ icon: 'info', title: 'No items found', text: 'No valid items to add.' });
			return;
		}

		const siteNumber = await ensurePurchaseRequestSiteReady(notification, purchaseRequestSubmission);
		if (!siteNumber) return;

		const confirm = await Swal.fire({
			icon: 'question',
			title: 'Add to cart?',
			text: `Add ${items.length} item(s) to cart for site ${siteNumber}?`,
			showCancelButton: true,
			confirmButtonText: 'Yes, Add to Cart',
			cancelButtonText: t('BUTTON.CANCEL'),
		});
		if (!confirm.isConfirmed) return;

		setAddingToCart(true);
		try {
			const products = items.map((item) => ({
				ProductID: item.product_id,
				Quantity: Number(item.quantity) || 1,
				Site: siteNumber,
			}));
			await addMultipleToCart(products);
			await Swal.fire({
				icon: 'success',
				title: 'Added to cart',
				text: `${products.length} item(s) added to cart.`,
				timer: 1800,
				showConfirmButton: false,
			});
		} finally {
			setAddingToCart(false);
		}
	}, [activePurchaseRequestNotification, addMultipleToCart, ensurePurchaseRequestSiteReady, purchaseRequestSubmission, t]);

	const handleAddPurchaseRequestItemsToRequestList = React.useCallback(async (items) => {
		const notification = activePurchaseRequestNotification;
		if (!notification) return;
		if (!items.length) {
			await Swal.fire({ icon: 'info', title: 'No items found', text: 'No valid items to add.' });
			return;
		}

		const missingMachine = items.filter((item) => !item.machine_id);
		if (missingMachine.length > 0) {
			await Swal.fire({
				icon: 'warning',
				title: 'Missing machine mapping',
				text: `${missingMachine.length} item(s) cannot be added to request list because machine_id is missing.`,
			});
			return;
		}

		const siteNumber = await ensurePurchaseRequestSiteReady(notification, purchaseRequestSubmission);
		if (!siteNumber) return;

		const confirm = await Swal.fire({
			icon: 'question',
			title: 'Add to request list?',
			text: `Add ${items.length} item(s) to request list for site ${siteNumber}?`,
			showCancelButton: true,
			confirmButtonText: 'Yes, Add to Request List',
			cancelButtonText: t('BUTTON.CANCEL'),
		});
		if (!confirm.isConfirmed) return;

		setAddingToRequestList(true);
		try {
			const payload = items.map((item) => ({
				user_id: user?.user_id,
				site_number: siteNumber,
				machine_id: Number(item.machine_id),
				product_id: Number(item.product_id),
			}));
			const response = await saveRequestListBulk(payload);
			const resultRows = Array.isArray(response?.data) ? response.data : [];
			const successCount = resultRows.filter((row) => row?.success).length;
			await Swal.fire({
				icon: successCount ? 'success' : 'info',
				title: 'Request list update',
				text: `${successCount}/${payload.length} item(s) added to request list.`,
			});
		} finally {
			setAddingToRequestList(false);
		}
	}, [activePurchaseRequestNotification, ensurePurchaseRequestSiteReady, purchaseRequestSubmission, t, user?.user_id]);

	const handleOrderPurchaseRequestItems = React.useCallback(async (items) => {
		const purchasableItems = items.filter((item) => item.purchasable);
		const nonPurchasableItems = items.filter((item) => !item.purchasable);
		let mode = null;

		if (purchasableItems.length && nonPurchasableItems.length) {
			const result = await Swal.fire({
				title: 'Order Items',
				text: 'Some items are non-purchasable. Choose how to proceed.',
				input: 'radio',
				inputOptions: {
					both: 'Add purchasable to Cart and non-purchasable to Request List',
					cart: 'Add only purchasable items to Cart',
					request: 'Add only non-purchasable items to Request List',
				},
				inputValue: 'both',
				showCancelButton: true,
				confirmButtonText: 'Submit',
				cancelButtonText: t('BUTTON.CANCEL'),
			});
			if (!result.isConfirmed) return;
			mode = result.value || 'both';
		} else if (purchasableItems.length) {
			mode = 'cart';
		} else if (nonPurchasableItems.length) {
			mode = 'request';
		} else {
			await Swal.fire({ icon: 'info', title: 'No valid items found' });
			return;
		}

		if (mode === 'both') {
			await handleAddPurchaseRequestItemsToCart(purchasableItems);
			await handleAddPurchaseRequestItemsToRequestList(nonPurchasableItems);
			navigate(ROUTES.CART);
			return;
		}
		if (mode === 'cart') {
			await handleAddPurchaseRequestItemsToCart(purchasableItems);
			navigate(ROUTES.CART);
			return;
		}
		await handleAddPurchaseRequestItemsToRequestList(nonPurchasableItems);
		navigate(ROUTES.REQUESTS);
	}, [handleAddPurchaseRequestItemsToCart, handleAddPurchaseRequestItemsToRequestList, navigate, t]);

	return (
		<Box sx={{ p: 2, width: '80vw', mx: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', minHeight: '650px' }}>
			<ModalWrapper
				open={ccrModalOpen}
				setOpen={setCcrModalOpen}
				title={t('GENERAL.CONTACT_CCR_SUBMISSION')}
				contentWidth={720}
				content={<>
					{ccrSubmission ? (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
								<Box sx={{ minWidth: 200 }}>
									<Typography variant="caption" color="text.secondary">Submitted</Typography>
									<Typography variant="body2">{formatDateTime(ccrSubmission.created_at)}</Typography>
								</Box>
								{ccrSubmission.site_number && (
									<Box sx={{ minWidth: 200 }}>
										<Typography variant="caption" color="text.secondary">Site</Typography>
										<Typography variant="body2">{ccrSubmission.site_name} - {ccrSubmission.site_number}</Typography>
									</Box>
								)}
							</Box>
							<Divider />
							<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
								<Box>
									<Typography variant="caption" color="text.secondary">{t('TABLE_HEADERS.NAME')}</Typography>
									<Typography variant="body2">{ccrSubmission.name || '-'}</Typography>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary">{t('TABLE_HEADERS.EMAIL')}</Typography>
									<Typography variant="body2">{ccrSubmission.email || '-'}</Typography>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary">{t('LABELS.PHONE')}</Typography>
									<Typography variant="body2">{ccrSubmission.phone || '-'}</Typography>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary">{t('TABLE_HEADERS.MACHINE_NUMBER')}</Typography>
									<Typography variant="body2">{ccrSubmission.machine_number || '-'}</Typography>
								</Box>
								<Box sx={{ gridColumn: '1 / -1' }}>
									<Typography variant="caption" color="text.secondary">{t('TABLE_HEADERS.SUBJECT')}</Typography>
									<Typography variant="body2">{ccrSubmission.subject || '-'}</Typography>
								</Box>
								<Box sx={{ gridColumn: '1 / -1' }}>
									<Typography variant="caption" color="text.secondary">{t('TABLE_HEADERS.MESSAGE')}</Typography>
									<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{ccrSubmission.message || '-'}</Typography>
								</Box>
							</Box>
						</Box>
					) : (
						<Alert severity="info">{t('ALERT_MESSAGES.NO_DETAILS_FOUND_FOR_SUBMISSION')}</Alert>
					)}
				</>}
			/>
			<ModalWrapper
				open={purchaseRequestModalOpen}
				setOpen={setPurchaseRequestModalOpen}
				title="Purchase Request"
				subtitle="Review request details, recipients, and requested parts."
				width="720px"
				content={
					(() => {
						const notification = activePurchaseRequestNotification;
						const data = notification?.data || {};
						const submission = purchaseRequestSubmission;
						const items = getPurchaseRequestItems(notification, submission);
						const siteNumber = resolvePurchaseRequestSiteNumber(notification, submission) || '-';
						const siteName = submission?.site?.site_name || data?.site_name || '-';
						const recipients = Array.isArray(submission?.recipients) ? submission.recipients : [];
						const requestedBy = getPurchaseRequestSender(notification, submission) || '-';
						const messageText = String(submission?.message || data?.message || notification?.body || '-');
						return (
							<Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2 }}>
								{purchaseRequestLoading && (
									<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
										<CircularProgress size={22} />
									</Box>
								)}
								<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
									<Box>
										<Typography variant="caption" color="text.secondary">Requested By</Typography>
										<Typography variant="body2">{requestedBy}</Typography>
									</Box>
									<Box>
										<Typography variant="caption" color="text.secondary">Site</Typography>
										<Typography variant="body2">{siteName} ({siteNumber})</Typography>
									</Box>
									<Box>
										<Typography variant="caption" color="text.secondary">Requested At</Typography>
										<Typography variant="body2">{notification?.created_at ? formatDateTime(notification.created_at) : '-'}</Typography>
									</Box>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary">Message</Typography>
									<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
										{messageText}
									</Typography>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary">Recipients</Typography>
									{recipients.length ? (
										<Stack spacing={0.75}>
											{recipients.map((recipient, idx) => (
												<Typography key={`${recipient.user_id || idx}`} variant="body2">
													{recipient.user_name || recipient.user_email || `User ${recipient.user_id}`} {recipient.user_email ? `(${recipient.user_email})` : ''} {recipient.role_type ? `- ${recipient.role_type}` : ''}
												</Typography>
											))}
										</Stack>
									) : (
										<Typography variant="body2">-</Typography>
									)}
								</Box>
								<Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5 }}>
									<Typography variant="subtitle2" sx={{ mb: 1 }}>
										Requested Parts ({items.length})
									</Typography>
									{items.length ? (
										<TableContainer sx={{ maxHeight: 320 }}>
											<Table size="small" stickyHeader>
												<TableHead>
													<TableRow>
														<TableCell>Part Name</TableCell>
														<TableCell align="right">Quantity</TableCell>
														<TableCell align="right">Price</TableCell>
														<TableCell align="right">Total</TableCell>
														<TableCell align="right">Actions</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{items.map((item, index) => (
														<TableRow key={`${item.product_id}-${index}`}>
															<TableCell>
																<Box sx={{ display: "flex", alignItems: "center", columnGap: 1 }}>
																	<img
																		src={resolveProductImageSrc(item)}
																		alt={item.product_name || item.product_number || "Part"}
																		onError={(event) => {
																			event.currentTarget.onerror = null;
																			event.currentTarget.src = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
																		}}
																		style={{ width: 28, height: 28, objectFit: "contain" }}
																	/>
																	<Box sx={{ minWidth: 0 }}>
																		{item.product_id && item.machine_id ? (
																			<Link
																				underline="hover"
																				sx={{
																					cursor: "pointer",
																					fontSize: "0.9rem",
																					display: "block",
																					maxWidth: 220,
																					whiteSpace: "nowrap",
																					overflow: "hidden",
																					textOverflow: "ellipsis",
																				}}
																				title={item.product_name || "-"}
																				onClick={() => navigate(`/machines/${item.machine_id}/${item.product_id}`)}
																			>
																				{item.product_name || '-'}
																			</Link>
																		) : (
																			<Typography
																				variant="body2"
																				sx={{
																					maxWidth: 220,
																					whiteSpace: "nowrap",
																					overflow: "hidden",
																					textOverflow: "ellipsis",
																				}}
																				title={item.product_name || "-"}
																			>
																				{item.product_name || '-'}
																			</Typography>
																		)}
																		<Typography
																			variant="caption"
																			color="text.secondary"
																			sx={{
																				display: "block",
																				maxWidth: 220,
																				whiteSpace: "nowrap",
																				overflow: "hidden",
																				textOverflow: "ellipsis",
																			}}
																			title={`Part #: ${item.product_number || item.product_id}`}
																		>
																			Part #: {item.product_number || item.product_id}
																		</Typography>
																	</Box>
																</Box>
															</TableCell>
															<TableCell align="right">x {item.quantity}</TableCell>
															<TableCell align="right">{formatCurrency(item.unit_price || 0, selectedSiteCurrency)}</TableCell>
															<TableCell align="right">{formatCurrency((item.unit_price || 0) * Number(item.quantity || 0), selectedSiteCurrency)}</TableCell>
															<TableCell align="right">
																<Stack direction="row" spacing={1} justifyContent="flex-end">
																	<Tooltip title={item.purchasable ? "Add to Cart" : "Add to Request List"}>
																		<span>
																			<IconButton
																				color="primary"
																				size="small"
																				onClick={() => {
																					if (item.purchasable) {
																						handleAddPurchaseRequestItemsToCart([item]);
																					} else {
																						handleAddPurchaseRequestItemsToRequestList([item]);
																					}
																				}}
																				disabled={addingToCart || addingToRequestList || (!item.purchasable && !item.machine_id)}
																			>
																				{item.purchasable ? <AddShoppingCart fontSize="small" /> : <SummarizeIcon fontSize="small" />}
																			</IconButton>
																		</span>
																	</Tooltip>
																</Stack>
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</TableContainer>
									) : (
										<Alert severity="info">No valid items in this request.</Alert>
									)}
								</Box>
							</Box>
						);
					})()
				}
				actions={
					<>
						<Button variant="outlined" onClick={() => setPurchaseRequestModalOpen(false)} disabled={addingToCart || addingToRequestList}>
							{t('BUTTON.CANCEL')}
						</Button>
						<Button
							variant="contained"
							onClick={() => handleOrderPurchaseRequestItems(getPurchaseRequestItems(activePurchaseRequestNotification, purchaseRequestSubmission))}
							disabled={addingToCart || addingToRequestList || !getPurchaseRequestItems(activePurchaseRequestNotification, purchaseRequestSubmission).length}
						>
							{addingToCart || addingToRequestList ? 'Processing...' : 'Order Items'}
						</Button>
					</>
				}
			/>
			<PageTitle title={t('UI_TEXT.NOTIFICATIONS_1')} />

			<ContextFilterAndSort
				options={sortOptions}
				selectedOption={sort}
				onSortChange={(val) => { setSort(val); setPage(1); }}
				placeholder={t('UI_TEXT.SEARCH_NOTIFICATIONS')}
				searchValue={q}
				onSearchChange={(val) => { setQ(val); setPage(1); }}
				showSort
				showSearch
			/>
			<Box sx={{ width: '100%', display: 'flex', columnGap: 1, alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>

				<Chip variant='outlined' size='small' sx={{ mt: unread ? 0 : 1 }} label={
					<>
						<Typography variant='body1' fontSize={"11px"} color='text.secondary'><b>{t('GENERAL.SHOWING_1')}:</b> {sort}</Typography>
					</>
				}>
				</Chip>
				{tab === 'unread' && <Button startIcon={<MarkEmailRead />} onClick={markAllLocal} disabled={unread === 0} variant="contained">{t('UI_TEXT.MARK_ALL_READ')}</Button>}
			</Box>


			{/* Read/Unread Tabs */}
			<Box sx={{ px: 0, pt: 1 }}>
				<Tabs value={tab} onChange={(_e, v) => setTab(v)} className="profile-tabs">
					<Tab value="unread" label={`${t('UI_TEXT.UNREAD_1')}${unread > 0 ? ` (${unread})` : ''}`} />
					<Tab value="read" label={`${t('GENERAL.READ_2')}${readCount > 0 ? ` (${readCount})` : ''}`} />
				</Tabs>
			</Box>

			<Box sx={{ pb: 2, pt: 1 }}>
				{paginatedItems.length === 0 ? (
					<Box sx={{ width: '100%' }}>
						<Alert severity='info' sx={{ width: '100%' }}>
							{t('UI_TEXT.NO_NOTIFICATIONS_YET')}
						</Alert>
					</Box>
				) : (
					<Stack spacing={1.5}>
						{paginatedItems.map((n, idx) => {
							const canAccess = hasAccessToNotificationSite(n);
							const target = canAccess ? getNotificationNavigationTarget(n) : null;
							const key = n.recipient_id || n.id || idx;
							const differentSite = isDifferentSite(n);
							const notifSite = resolveNotificationSiteNumber(n);
							const isPurchaseRequest = isPurchaseRequestNotification(n);
							const isClickable = Boolean(target || isPurchaseRequest);
							return (
								<Box
									key={key}
									role={isClickable ? "button" : undefined}
									tabIndex={isClickable ? 0 : undefined}
									onClick={async () => {
										if (!n.read_at && n.recipient_id) {
											await markOne(n.recipient_id);
										}
										if (isPurchaseRequest) {
											handleOpenPurchaseRequestNotification(n);
											return;
										}
										handleNavigateNotification(n, target);
									}}
									onKeyDown={(event) => {
										if (!isClickable) return;
										if (event.key === "Enter" || event.key === " ") {
											event.preventDefault();
											if (isPurchaseRequest) {
												handleOpenPurchaseRequestNotification(n);
												return;
											}
											handleNavigateNotification(n, target);
										}
									}}
									sx={{
										width: "100%",
										maxHeight: "75px",
										borderRadius: 2,
										display: "flex",
										flexDirection: "row",
										justifyContent: "space-between",
										alignItems: "center",
										backgroundColor: "#fff",
										boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)",
										p: 2,
										columnGap: 2,
										margin: "7px 0",
										cursor: isClickable ? "pointer" : "default",
										outline: "none",
										borderLeft: differentSite ? '3px solid #f59e0b' : '3px solid transparent',
										backgroundColor: differentSite ? 'rgba(245,158,11,0.08)' : '#fff',
									}}
								>
									<Stack direction="row" spacing={1.5} alignItems="center">
										<Box sx={{ mt: 0.2 }}>{iconFor(n.event_key)}</Box>
										<Box>
											<Stack direction="row" spacing={1} alignItems="center">
												<Typography variant="body1" fontWeight={700} sx={{ color: n.read_at ? "text.secondary" : "text.primary" }}>{n.title || n.event_key}</Typography>
											</Stack>
											<Typography variant="body2" color="text.secondary">
												{(() => {
													const desc = n.description || (n.data && n.data.description) || "";
													const eventKey = String(n?.event_key || "").toUpperCase();
													const isPurchaseRequest = eventKey === NotificationEvents.USER_PURCHASE_REQUEST_SUBMITTED;
													const parts = [];
													if (desc) parts.push(desc);
													if (isPurchaseRequest) {
														const compact = buildPurchaseRequestCardSummary(n);
														if (compact) parts.push(compact);
													} else if (n.body) {
														parts.push(n.body);
													}
													if (differentSite && notifSite) parts.push(`Site: ${notifSite}`);
													parts.push(formatTime(n.created_at));
													return parts.filter(Boolean).join(" • ");
												})()}
											</Typography>
											{differentSite && (
												<Typography fontSize="11px" fontStyle="italic" color="warning.main">
													Different Customer Location
												</Typography>
											)}
										</Box>
									</Stack>
									<Box sx={{ display: "flex", alignItems: "center", columnGap: 0 }}>
										{n.read_at ? (
											<IconButton
												onClick={(event) => {
													event.stopPropagation();
													deleteOne(n.recipient_id);
												}}
												color='error'
												disabled={!n.recipient_id}
											>
												<Delete />
											</IconButton>
										) : (
											<IconButton
												onClick={(event) => {
													event.stopPropagation();
														markOne(n.recipient_id);
													}}
													disabled={!n.recipient_id}
												>
													<MarkEmailRead color={"success"} />
												</IconButton>
										)}

									</Box>
								</Box>
							);
						})}
					</Stack>
				)}
			</Box>

			{pageCount > 1 && (
				<Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
					<Pagination
						size="small"
						count={pageCount}
						page={page}
						onChange={handlePageChange}
					/>
				</Box>
			)}
		</Box>
	);
};

export default NotificationsPage;
